import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  MessageCircle, Send, Users, CheckCircle, 
  RefreshCw, QrCode, Wallet, Eye, FileText, Image as ImageIcon, 
  Phone, Loader2, History, AlertTriangle, List, Filter, X,
  Download, Edit3
} from 'lucide-react';

// Mohalla order for dropdown
const MOHALLA_ORDER = ["QUTBI MOHALLA", "NAJMI MOHALLA", "FAKHRI MOHALLA", "FATEMI MOHALLA", "HASANI MOHALLA", "HASHMI MOHALLA"];

// Base Personalization placeholders
const BASE_PLACEHOLDERS = [
  { key: '{{FullName}}', label: 'Full Name' },
  { key: '{{HOFName}}', label: 'HOF Name' },
  { key: '{{Mobile}}', label: 'Mobile' },
  { key: '{{Age}}', label: 'Age' },
  { key: '{{Gender}}', label: 'Gender' },
  { key: '{{BloodGroup}}', label: 'Blood Group' },
  { key: '{{MaritalStatus}}', label: 'Marital Status' },
  { key: '{{MisaqStatus}}', label: 'Misaq Status' },
  { key: '{{Mohalla}}', label: 'Mohalla' },
  { key: '{{Sector}}', label: 'Sector' },
  { key: '{{ThaaliNumber}}', label: 'Thaali Number' },
  { key: '{{FMBStatus}}', label: 'FMB Status' },
];

export default function MessagingPage() {
  const { api, isSuperAdmin } = useAuth();
  const textareaRef = useRef(null);
  
  // WhatsApp Status
  const [waStatus, setWaStatus] = useState({ status: 'checking' });
  const [balance, setBalance] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [qrUrls, setQrUrls] = useState(null);
  
  // Recipient Selection Mode: 'list', 'lists', 'filter', 'organization', or 'manual'
  const [selectionMode, setSelectionMode] = useState('list');
  const [customLists, setCustomLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [selectedListIds, setSelectedListIds] = useState([]); // Multiple list selection
  const [selectedListName, setSelectedListName] = useState('');
  const [selectedListColumns, setSelectedListColumns] = useState([]); // Custom columns from selected list
  
  // Organization Lists
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  
  // Manual Numbers
  const [manualNumbers, setManualNumbers] = useState('');
  const [manualNumbersCount, setManualNumbersCount] = useState(0);
  
  // Filter State
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [filteredSectors, setFilteredSectors] = useState([]);
  const [filters, setFilters] = useState({
    mohalla: '',
    sector: '',
    gender: 'All',
    misaq_status: 'All',
    min_age: '',
    max_age: '',
    hof_only: false
  });
  
  // Recipients
  const [recipients, setRecipients] = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [recipientsLocked, setRecipientsLocked] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  
  // Message Composition
  const [messageTemplate, setMessageTemplate] = useState('');
  const [attachedPdf, setAttachedPdf] = useState(null);
  const [attachedImage, setAttachedImage] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  
  // All placeholders (base + custom from list)
  const [allPlaceholders, setAllPlaceholders] = useState(BASE_PLACEHOLDERS);
  
  // Preview & Send
  const [showPreview, setShowPreview] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [showConfirmSend, setShowConfirmSend] = useState(false);
  
  // History
  const [batches, setBatches] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Check WhatsApp status
  const checkStatus = useCallback(async () => {
    try {
      const response = await api.get('/whatsapp/status');
      setWaStatus(response.data);
    } catch (error) {
      setWaStatus({ status: 'error', message: 'Failed to check status' });
    }
  }, [api]);

  // Get balance
  const getBalance = useCallback(async () => {
    try {
      const response = await api.get('/whatsapp/balance');
      setBalance(response.data.balance);
    } catch (error) {
      console.error('Failed to get balance');
    }
  }, [api]);

  // Get QR code
  const getQRCode = async () => {
    try {
      const response = await api.get('/whatsapp/qr');
      setQrUrls(response.data);
      setShowQR(true);
    } catch (error) {
      toast.error('Failed to get QR code');
    }
  };

  // Reconnect
  const handleReconnect = async () => {
    try {
      await api.post('/whatsapp/reconnect');
      toast.success('Reconnect initiated');
      setTimeout(checkStatus, 3000);
    } catch (error) {
      toast.error('Failed to reconnect');
    }
  };

  // Fetch initial data
  const fetchInitialData = useCallback(async () => {
    try {
      const [listsRes, mohallasRes, sectorsRes, orgsRes] = await Promise.all([
        api.get('/lists'),
        api.get('/mohallas'),
        api.get('/sectors'),
        api.get('/lists/auto/organizations').catch(() => ({ data: { organizations: [] } }))
      ]);
      
      setCustomLists(listsRes.data.lists || []);
      setOrganizations(orgsRes.data.organizations || []);
      
      // Sort mohallas by fixed order
      const sortedMohallas = (mohallasRes.data.mohallas || []).sort((a, b) => {
        const aIndex = MOHALLA_ORDER.indexOf((a.name || a).toUpperCase());
        const bIndex = MOHALLA_ORDER.indexOf((b.name || b).toUpperCase());
        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
      });
      setMohallas(sortedMohallas);
      setSectors(sectorsRes.data.sectors || []);
    } catch (error) {
      console.error('Failed to fetch initial data');
    }
  }, [api]);

  // Fetch messaging history
  const fetchHistory = useCallback(async () => {
    try {
      setLogsLoading(true);
      const [batchesRes, logsRes] = await Promise.all([
        api.get('/messaging/batches'),
        isSuperAdmin ? api.get('/messaging/logs?limit=100') : Promise.resolve({ data: { logs: [] } })
      ]);
      setBatches(batchesRes.data.batches || []);
      setLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch history');
    } finally {
      setLogsLoading(false);
    }
  }, [api, isSuperAdmin]);

  useEffect(() => {
    checkStatus();
    getBalance();
    fetchInitialData();
    fetchHistory();
  }, [checkStatus, getBalance, fetchInitialData, fetchHistory]);

  // Filter sectors by mohalla
  useEffect(() => {
    if (filters.mohalla) {
      const filtered = sectors.filter(s => {
        const sectorMohalla = s.mohalla || '';
        return sectorMohalla.toUpperCase().includes(filters.mohalla.toUpperCase());
      });
      setFilteredSectors(filtered);
    } else {
      setFilteredSectors(sectors);
    }
    // Clear sector if mohalla changed
    if (filters.sector && filters.mohalla) {
      const sectorExists = sectors.some(s => 
        (s.name || s) === filters.sector && 
        (s.mohalla || '').toUpperCase().includes(filters.mohalla.toUpperCase())
      );
      if (!sectorExists) {
        setFilters(prev => ({ ...prev, sector: '' }));
      }
    }
  }, [filters.mohalla, filters.sector, sectors]);

  // Update placeholders when list changes
  useEffect(() => {
    if (selectedListColumns.length > 0) {
      const customPlaceholders = selectedListColumns.map(col => ({
        key: `{{Custom:${col.name}}}`,
        label: col.name,
        isCustom: true,
        columnId: col.id
      }));
      setAllPlaceholders([...BASE_PLACEHOLDERS, ...customPlaceholders]);
    } else {
      setAllPlaceholders(BASE_PLACEHOLDERS);
    }
  }, [selectedListColumns]);

  // Parse manual numbers and count
  useEffect(() => {
    if (manualNumbers.trim()) {
      const numbers = manualNumbers
        .split(/[\n,;]+/)
        .map(n => n.trim().replace(/\s+/g, ''))
        .filter(n => n.length >= 10);
      setManualNumbersCount(numbers.length);
    } else {
      setManualNumbersCount(0);
    }
  }, [manualNumbers]);

  // Handle list selection
  const handleListSelect = async (listId) => {
    setSelectedListId(listId);
    const selectedList = customLists.find(l => l.id === listId);
    setSelectedListName(selectedList?.name || '');
    setSelectionMode('list');
    setRecipientsLocked(false);
    setManualNumbers('');
    
    if (listId) {
      setLoadingRecipients(true);
      try {
        const response = await api.get(`/lists/${listId}`);
        const members = response.data.members || [];
        const columns = response.data.custom_columns || [];
        
        setRecipients(members);
        setRecipientCount(members.length);
        setSelectedListColumns(columns);
        setRecipientsLocked(true);
      } catch (error) {
        toast.error('Failed to load list members');
        setSelectedListColumns([]);
      } finally {
        setLoadingRecipients(false);
      }
    } else {
      setSelectedListColumns([]);
    }
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setRecipientsLocked(false);
    setRecipientCount(0);
    setSelectedListColumns([]);
  };

  // Get recipients based on current selection mode
  const getFilteredRecipients = async () => {
    setLoadingRecipients(true);
    
    try {
      if (selectionMode === 'list' && selectedListId) {
        // Single list mode
        const response = await api.get(`/lists/${selectedListId}`);
        const members = response.data.members || [];
        const columns = response.data.custom_columns || [];
        
        setRecipients(members);
        setRecipientCount(members.length);
        setSelectedListColumns(columns);
        setRecipientsLocked(true);
      } else if (selectionMode === 'lists' && selectedListIds.length > 0) {
        // Multiple lists mode - need to aggregate
        let allMembers = [];
        let seenIds = new Set();
        
        for (const listId of selectedListIds) {
          const response = await api.get(`/lists/${listId}`);
          const members = response.data.members || [];
          for (const m of members) {
            if (!seenIds.has(m.id)) {
              seenIds.add(m.id);
              allMembers.push(m);
            }
          }
        }
        
        setRecipients(allMembers);
        setRecipientCount(allMembers.length);
        setSelectedListColumns([]);
        setRecipientsLocked(true);
      } else if (selectionMode === 'organization' && selectedOrganization) {
        // Organization mode
        const response = await api.get(`/lists/auto/organization/${encodeURIComponent(selectedOrganization)}`);
        setRecipients(response.data.members || []);
        setRecipientCount(response.data.total || 0);
        setSelectedListColumns([]);
        setRecipientsLocked(true);
      } else if (selectionMode === 'filter') {
        // Filter mode
        const filterPayload = {};
        if (filters.mohalla) filterPayload.mohalla = filters.mohalla;
        if (filters.sector) filterPayload.sector = filters.sector;
        if (filters.gender !== 'All') filterPayload.gender = filters.gender;
        if (filters.misaq_status !== 'All') filterPayload.misaq_status = filters.misaq_status;
        if (filters.min_age) filterPayload.min_age = parseInt(filters.min_age);
        if (filters.max_age) filterPayload.max_age = parseInt(filters.max_age);
        if (filters.hof_only) filterPayload.hof_only = true;
        
        const response = await api.post('/messaging/filter-recipients', filterPayload);
        setRecipients(response.data.recipients || []);
        setRecipientCount(response.data.total || 0);
        setSelectedListColumns([]);
        setRecipientsLocked(true);
      }
    } catch (error) {
      console.error('Failed to get recipients:', error);
      toast.error('Failed to get recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  // Handle manual numbers mode
  const handleUseManualNumbers = () => {
    if (!manualNumbers.trim()) {
      toast.error('Please enter at least one phone number');
      return;
    }
    
    const numbers = manualNumbers
      .split(/[\n,;]+/)
      .map(n => n.trim().replace(/\s+/g, ''))
      .filter(n => n.length >= 10);
    
    if (numbers.length === 0) {
      toast.error('No valid phone numbers found');
      return;
    }
    
    setSelectionMode('manual');
    setSelectedListId('');
    setSelectedListName('');
    setSelectedListColumns([]);
    setRecipientCount(numbers.length);
    setRecipientsLocked(true);
    toast.success(`${numbers.length} phone numbers ready`);
  };

  // Clear manual numbers
  const clearManualNumbers = () => {
    setManualNumbers('');
    setManualNumbersCount(0);
    if (selectionMode === 'manual') {
      setRecipientsLocked(false);
      setRecipientCount(0);
    }
  };

  // Insert placeholder at cursor
  const insertPlaceholder = (placeholder) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = messageTemplate;
    const newText = text.substring(0, start) + placeholder + text.substring(end);
    
    setMessageTemplate(newText);
    
    // Set cursor position after placeholder
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  // Handle file upload
  const handleFileUpload = async (file, type) => {
    if (!file) return null;
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload/whatsapp-attachment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      return `${baseUrl}${response.data.url}`;
    } catch (error) {
      toast.error('Failed to upload file');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (type === 'pdf' && !file.type.includes('pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    if (type === 'image' && !file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    const url = await handleFileUpload(file, type);
    if (url) {
      if (type === 'pdf') {
        setAttachedPdf({ name: file.name, url });
      } else {
        setAttachedImage({ name: file.name, url, preview: URL.createObjectURL(file) });
      }
    }
  };

  // Remove attachment
  const removeAttachment = (type) => {
    if (type === 'pdf') {
      setAttachedPdf(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } else {
      if (attachedImage?.preview) URL.revokeObjectURL(attachedImage.preview);
      setAttachedImage(null);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  // Preview messages
  const handlePreview = async () => {
    if (!messageTemplate.trim()) {
      toast.error('Please enter a message template');
      return;
    }
    if (!recipientsLocked || recipientCount === 0) {
      toast.error('Please select recipients first');
      return;
    }
    
    // For manual mode, show simple preview
    if (selectionMode === 'manual') {
      const numbers = manualNumbers
        .split(/[\n,;]+/)
        .map(n => n.trim().replace(/\s+/g, ''))
        .filter(n => n.length >= 10);
      
      setPreviews(numbers.slice(0, 3).map(num => ({
        recipient_name: 'Manual Entry',
        recipient_mobile: num,
        personalized_message: messageTemplate // No personalization for manual numbers
      })));
      setShowPreview(true);
      return;
    }
    
    setLoadingPreview(true);
    try {
      const payload = {
        recipient_source: selectionMode,
        message_template: messageTemplate
      };
      
      if (selectionMode === 'list') {
        payload.list_id = selectedListId;
      } else if (selectionMode === 'lists') {
        payload.list_ids = selectedListIds;
      } else if (selectionMode === 'organization') {
        payload.organization_name = selectedOrganization;
      } else {
        const filterPayload = {};
        if (filters.mohalla) filterPayload.mohalla = filters.mohalla;
        if (filters.sector) filterPayload.sector = filters.sector;
        if (filters.gender !== 'All') filterPayload.gender = filters.gender;
        if (filters.misaq_status !== 'All') filterPayload.misaq_status = filters.misaq_status;
        if (filters.min_age) filterPayload.min_age = parseInt(filters.min_age);
        if (filters.max_age) filterPayload.max_age = parseInt(filters.max_age);
        if (filters.hof_only) filterPayload.hof_only = true;
        payload.filters = filterPayload;
      }
      
      const response = await api.post('/messaging/preview', payload);
      setPreviews(response.data.previews || []);
      setShowPreview(true);
    } catch (error) {
      console.error('[Preview] Error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Send messages
  const handleSend = async () => {
    if (!messageTemplate.trim()) {
      toast.error('Please enter a message template');
      return;
    }
    if (!recipientsLocked || recipientCount === 0) {
      toast.error('Please select recipients first');
      return;
    }
    
    setSending(true);
    try {
      // For manual mode, use different endpoint
      if (selectionMode === 'manual') {
        const numbers = manualNumbers
          .split(/[\n,;]+/)
          .map(n => n.trim().replace(/\s+/g, ''))
          .filter(n => n.length >= 10);
        
        const response = await api.post('/messaging/send-manual', {
          phone_numbers: numbers,
          message: messageTemplate,
          pdf_url: attachedPdf?.url || null,
          image_url: attachedImage?.url || null
        });
        
        toast.success(`Sending messages to ${response.data.total_recipients} numbers`);
      } else {
        // Existing logic for list/filter/organization mode
        const payload = {
          recipient_source: selectionMode,
          message_template: messageTemplate,
          pdf_url: attachedPdf?.url || null,
          image_url: attachedImage?.url || null
        };
        
        if (selectionMode === 'list') {
          payload.list_id = selectedListId;
        } else if (selectionMode === 'lists') {
          payload.list_ids = selectedListIds;
        } else if (selectionMode === 'organization') {
          payload.organization_name = selectedOrganization;
        } else {
          const filterPayload = {};
          if (filters.mohalla) filterPayload.mohalla = filters.mohalla;
          if (filters.sector) filterPayload.sector = filters.sector;
          if (filters.gender !== 'All') filterPayload.gender = filters.gender;
          if (filters.misaq_status !== 'All') filterPayload.misaq_status = filters.misaq_status;
          if (filters.min_age) filterPayload.min_age = parseInt(filters.min_age);
          if (filters.max_age) filterPayload.max_age = parseInt(filters.max_age);
          if (filters.hof_only) filterPayload.hof_only = true;
          payload.filters = filterPayload;
        }
        
        const response = await api.post('/messaging/send-personalized', payload);
        toast.success(`Sending personalized messages to ${response.data.total_recipients} recipients`);
      }
      
      // Reset form
      setShowConfirmSend(false);
      setMessageTemplate('');
      removeAttachment('pdf');
      removeAttachment('image');
      setRecipientsLocked(false);
      setRecipientCount(0);
      setSelectedListId('');
      setSelectedListIds([]);
      setSelectedOrganization('');
      setSelectedListColumns([]);
      setManualNumbers('');
      setFilters({
        mohalla: '',
        sector: '',
        gender: 'All',
        misaq_status: 'All',
        min_age: '',
        max_age: '',
        hof_only: false
      });
      
      // Refresh history
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send messages');
    } finally {
      setSending(false);
    }
  };

  // Export logs
  const handleExportLogs = async () => {
    try {
      const response = await api.get('/export/messaging-logs', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `messaging_logs_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to export logs');
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="messaging-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <MessageCircle className="w-7 h-7" />
            WhatsApp Messaging
          </h1>
          <p className="text-slate-600 text-sm mt-1">Send personalized messages to families and members</p>
        </div>
        
        {/* Status Card */}
        <Card className="sm:w-auto">
          <CardContent className="p-3 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                waStatus.status === 'connected' ? 'bg-green-500' : 
                waStatus.status === 'error' ? 'bg-red-500' : 'bg-yellow-500 animate-pulse'
              }`} />
              <span className="text-sm font-medium" data-testid="wa-status">
                {waStatus.status === 'connected' ? 'Connected' : 
                 waStatus.status === 'error' ? 'Disconnected' : 'Checking...'}
              </span>
            </div>
            {balance && (
              <div className="flex items-center gap-1 text-sm">
                <Wallet className="w-4 h-4 text-amber-600" />
                <span className="font-medium">{balance}</span>
              </div>
            )}
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={checkStatus} title="Refresh Status">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={getQRCode} title="Scan QR">
                <QrCode className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleReconnect} title="Reconnect">
                <Phone className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Warning if not connected */}
      {waStatus.status !== 'connected' && waStatus.status !== 'checking' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">WhatsApp Not Connected</p>
            <p className="text-sm text-amber-700">Please scan the QR code to connect your WhatsApp.</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="compose" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
          <TabsTrigger value="compose" className="gap-2" data-testid="compose-tab">
            <Send className="w-4 h-4" />
            <span>Compose</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2" data-testid="history-tab">
            <History className="w-4 h-4" />
            <span>History</span>
          </TabsTrigger>
        </TabsList>

        {/* Compose Tab */}
        <TabsContent value="compose" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left Panel - Select Recipients */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Select Recipients
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Selection Mode Tabs */}
                <div className="flex flex-wrap gap-2 pb-2">
                  <Button
                    variant={selectionMode === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setSelectionMode('list'); setRecipientsLocked(false); setRecipientCount(0); }}
                  >
                    <List className="w-4 h-4 mr-1" />
                    Single List
                  </Button>
                  <Button
                    variant={selectionMode === 'lists' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setSelectionMode('lists'); setRecipientsLocked(false); setRecipientCount(0); }}
                  >
                    <List className="w-4 h-4 mr-1" />
                    Multiple Lists
                  </Button>
                  {organizations.length > 0 && (
                    <Button
                      variant={selectionMode === 'organization' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setSelectionMode('organization'); setRecipientsLocked(false); setRecipientCount(0); }}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Organization
                    </Button>
                  )}
                  <Button
                    variant={selectionMode === 'filter' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setSelectionMode('filter'); setRecipientsLocked(false); setRecipientCount(0); }}
                  >
                    <Filter className="w-4 h-4 mr-1" />
                    Filter
                  </Button>
                  <Button
                    variant={selectionMode === 'manual' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setSelectionMode('manual'); setRecipientsLocked(false); setRecipientCount(0); }}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Manual
                  </Button>
                </div>

                {/* Single List Selection */}
                {selectionMode === 'list' && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <List className="w-4 h-4 text-slate-500" />
                      <Label className="font-medium">Select a List</Label>
                    </div>
                    <Select 
                      value={selectedListId} 
                      onValueChange={handleListSelect}
                      data-testid="list-select"
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a list..." />
                      </SelectTrigger>
                      <SelectContent>
                        {customLists.map(list => (
                          <SelectItem key={list.id} value={list.id}>
                            {list.name} ({list.member_count || 0})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedListName && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {selectedListName}
                        {selectedListColumns.length > 0 && (
                          <span className="text-slate-500 ml-1">
                            ({selectedListColumns.length} custom columns)
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                )}

                {/* Multiple Lists Selection */}
                {selectionMode === 'lists' && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <List className="w-4 h-4 text-slate-500" />
                      <Label className="font-medium">Select Multiple Lists</Label>
                    </div>
                    <div className="border rounded-md p-2 max-h-60 overflow-y-auto space-y-2">
                      {customLists.map(list => (
                        <div key={list.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`list-${list.id}`}
                            checked={selectedListIds.includes(list.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedListIds(prev => [...prev, list.id]);
                              } else {
                                setSelectedListIds(prev => prev.filter(id => id !== list.id));
                              }
                              setRecipientsLocked(false);
                              setRecipientCount(0);
                            }}
                          />
                          <label
                            htmlFor={`list-${list.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {list.name} ({list.member_count || 0})
                          </label>
                        </div>
                      ))}
                    </div>
                    {selectedListIds.length > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        <CheckCircle className="w-3 h-3 inline mr-1" />
                        {selectedListIds.length} lists selected
                      </p>
                    )}
                  </div>
                )}

                {/* Organization Selection */}
                {selectionMode === 'organization' && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-slate-500" />
                      <Label className="font-medium">Select Organization</Label>
                    </div>
                    <Select 
                      value={selectedOrganization} 
                      onValueChange={(v) => {
                        setSelectedOrganization(v);
                        setRecipientsLocked(false);
                        setRecipientCount(0);
                      }}
                      data-testid="organization-select"
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose an organization..." />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map(org => (
                          <SelectItem key={org.name} value={org.name}>
                            {org.name} ({org.member_count} members)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedOrganization && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {selectedOrganization}
                      </p>
                    )}
                  </div>
                )}

                {/* Filter Mode */}
                {selectionMode === 'filter' && (
                  <>
                    {/* Filters */}
                    <div className="space-y-3">
                      {/* Mohalla */}
                      <div>
                        <Label className="text-sm">Mohalla</Label>
                        <Select 
                          value={filters.mohalla || "all_mohallas"} 
                          onValueChange={(v) => handleFilterChange('mohalla', v === "all_mohallas" ? '' : v)}
                        >
                          <SelectTrigger data-testid="mohalla-filter">
                            <SelectValue placeholder="Select Mohalla" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all_mohallas">All Mohallas</SelectItem>
                            {mohallas.map(m => (
                              <SelectItem key={m.name || m} value={m.name || m}>{m.name || m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                  {/* Sector */}
                  <div>
                    <Label className="text-sm">Sector</Label>
                    <Select 
                      value={filters.sector || "all_sectors"} 
                      onValueChange={(v) => handleFilterChange('sector', v === "all_sectors" ? '' : v)}
                    >
                      <SelectTrigger data-testid="sector-filter">
                        <SelectValue placeholder="Select Sector" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_sectors">All Sectors</SelectItem>
                        {filteredSectors.map(s => (
                          <SelectItem key={s.name || s} value={s.name || s}>
                            {s.name || s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Gender */}
                  <div>
                    <Label className="text-sm">Gender</Label>
                    <Select 
                      value={filters.gender} 
                      onValueChange={(v) => handleFilterChange('gender', v)}
                    >
                      <SelectTrigger data-testid="gender-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">All Genders</SelectItem>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Misaq Status */}
                  <div>
                    <Label className="text-sm">Misaq Status</Label>
                    <Select 
                      value={filters.misaq_status} 
                      onValueChange={(v) => handleFilterChange('misaq_status', v)}
                    >
                      <SelectTrigger data-testid="misaq-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">All</SelectItem>
                        <SelectItem value="Done">Done</SelectItem>
                        <SelectItem value="Not Done">Not Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Age Range */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-sm">Age From</Label>
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.min_age}
                        onChange={(e) => handleFilterChange('min_age', e.target.value)}
                        data-testid="min-age-filter"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Age To</Label>
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.max_age}
                        onChange={(e) => handleFilterChange('max_age', e.target.value)}
                        data-testid="max-age-filter"
                      />
                    </div>
                  </div>

                  {/* HOF Only */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="hof-only"
                      checked={filters.hof_only}
                      onCheckedChange={(checked) => handleFilterChange('hof_only', checked)}
                      data-testid="hof-only-filter"
                    />
                    <label htmlFor="hof-only" className="text-sm font-medium cursor-pointer">
                      HOF Only
                    </label>
                  </div>
                </div>

                {/* Get Recipients Button for Filter Mode */}
                <Button 
                  onClick={getFilteredRecipients}
                  disabled={loadingRecipients}
                  className="w-full bg-navy hover:bg-navy/90"
                  data-testid="get-recipients-btn"
                >
                  {loadingRecipients ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                  ) : (
                    <><Filter className="w-4 h-4 mr-2" />Get Recipients</>
                  )}
                </Button>
                  </>
                )}

                {/* Manual Number Entry Mode */}
                {selectionMode === 'manual' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Edit3 className="w-4 h-4 text-slate-500" />
                        <Label className="font-medium">Enter Phone Numbers</Label>
                      </div>
                      {manualNumbersCount > 0 && (
                        <Badge variant="secondary">{manualNumbersCount} numbers</Badge>
                      )}
                    </div>
                    <Textarea
                      placeholder="Enter phone numbers (one per line, or comma/semicolon separated)&#10;&#10;Example:&#10;9876543210&#10;9123456789, 9111222333"
                      value={manualNumbers}
                      onChange={(e) => setManualNumbers(e.target.value)}
                      rows={6}
                      className="font-mono text-sm"
                      data-testid="manual-numbers-input"
                    />
                    <div className="flex gap-2 mt-2">
                      <Button
                        onClick={handleUseManualNumbers}
                        disabled={!manualNumbers.trim()}
                        size="sm"
                        className="flex-1"
                        data-testid="use-manual-numbers-btn"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Use These Numbers
                      </Button>
                      {manualNumbers && (
                        <Button
                          onClick={clearManualNumbers}
                          size="sm"
                          variant="outline"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Get Recipients Button for List/Organization modes */}
                {(selectionMode === 'list' || selectionMode === 'lists' || selectionMode === 'organization') && (
                  <Button 
                    onClick={getFilteredRecipients}
                    disabled={loadingRecipients || 
                      (selectionMode === 'list' && !selectedListId) ||
                      (selectionMode === 'lists' && selectedListIds.length === 0) ||
                      (selectionMode === 'organization' && !selectedOrganization)
                    }
                    className="w-full bg-navy hover:bg-navy/90"
                    data-testid="get-recipients-btn"
                  >
                    {loadingRecipients ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                    ) : (
                      <><Users className="w-4 h-4 mr-2" />Get Recipients</>
                    )}
                  </Button>
                )}

                {/* Recipients Count */}
                {recipientsLocked && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="recipients-count">
                    <p className="text-sm text-green-800 font-medium">
                      {recipientCount} {selectionMode === 'manual' ? 'phone numbers' : 'recipients'} selected
                      {selectionMode === 'manual' && (
                        <span className="text-xs block text-green-600 mt-1">
                          (Manual entry - no personalization)
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right Panel - Compose Message */}
            <Card className="lg:col-span-3">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Compose Message
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Personalization Placeholders */}
                <div>
                  <Label className="font-medium mb-2 block">
                    Personalization Placeholders
                    {selectionMode === 'manual' && (
                      <span className="text-xs text-amber-600 ml-2">(disabled for manual numbers)</span>
                    )}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {allPlaceholders.map(p => (
                      <Badge
                        key={p.key}
                        variant={p.isCustom ? "default" : "outline"}
                        className={`cursor-pointer transition-colors ${
                          selectionMode === 'manual' 
                            ? 'opacity-50 cursor-not-allowed' 
                            : p.isCustom 
                              ? 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200' 
                              : 'hover:bg-blue-50 hover:border-blue-300'
                        }`}
                        onClick={() => selectionMode !== 'manual' && insertPlaceholder(p.key)}
                        data-testid={`placeholder-${p.key.replace(/[{}:]/g, '')}`}
                      >
                        {p.key}
                      </Badge>
                    ))}
                  </div>
                  {selectedListColumns.length > 0 && (
                    <p className="text-xs text-purple-600 mt-2">
                      Purple badges are custom columns from the selected list
                    </p>
                  )}
                </div>

                {/* Message Template */}
                <div>
                  <Label className="font-medium">Message Template</Label>
                  <Textarea
                    ref={textareaRef}
                    placeholder="Type your message here... Use placeholders above for personalization."
                    value={messageTemplate}
                    onChange={(e) => setMessageTemplate(e.target.value)}
                    rows={8}
                    className="mt-2 font-mono text-sm"
                    data-testid="message-template"
                  />
                  <p className="text-xs text-slate-500 mt-1">{messageTemplate.length} characters</p>
                </div>

                {/* Attachments */}
                <div>
                  <Label className="font-medium mb-2 block">Attachments (Optional)</Label>
                  <div className="flex flex-wrap gap-3">
                    {/* PDF */}
                    <div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".pdf"
                        onChange={(e) => handleFileSelect(e, 'pdf')}
                        className="hidden"
                      />
                      {attachedPdf ? (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                          <FileText className="w-4 h-4 text-red-600" />
                          <span className="text-sm text-red-700 max-w-[150px] truncate">{attachedPdf.name}</span>
                          <button onClick={() => removeAttachment('pdf')} className="text-red-500 hover:text-red-700">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFile}
                          data-testid="attach-pdf-btn"
                        >
                          <FileText className="w-4 h-4 mr-2 text-red-600" />
                          Attach PDF
                        </Button>
                      )}
                    </div>

                    {/* Image */}
                    <div>
                      <input
                        type="file"
                        ref={imageInputRef}
                        accept="image/*"
                        onChange={(e) => handleFileSelect(e, 'image')}
                        className="hidden"
                      />
                      {attachedImage ? (
                        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                          {attachedImage.preview && (
                            <img src={attachedImage.preview} alt="Preview" className="w-8 h-8 object-cover rounded" />
                          )}
                          <span className="text-sm text-blue-700 max-w-[150px] truncate">{attachedImage.name}</span>
                          <button onClick={() => removeAttachment('image')} className="text-blue-500 hover:text-blue-700">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => imageInputRef.current?.click()}
                          disabled={uploadingFile}
                          data-testid="attach-image-btn"
                        >
                          <ImageIcon className="w-4 h-4 mr-2 text-blue-600" />
                          Attach Image
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={handlePreview}
                    disabled={loadingPreview || !messageTemplate.trim() || !recipientsLocked}
                    data-testid="preview-btn"
                  >
                    {loadingPreview ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    Preview
                  </Button>
                  <Button
                    onClick={() => setShowConfirmSend(true)}
                    disabled={!messageTemplate.trim() || !recipientsLocked || recipientCount === 0}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="send-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send via WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          {/* Recent Batches */}
          {batches.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Recent Message Batches</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {batches.slice(0, 10).map(batch => (
                    <div key={batch.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {batch.source === 'list' ? 'List' : batch.source === 'manual' ? 'Manual' : 'Filtered'} Recipients
                        </p>
                        <p className="text-xs text-slate-500">{new Date(batch.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{batch.total} total</Badge>
                        <Badge className="bg-green-100 text-green-700">{batch.sent} sent</Badge>
                        {batch.failed > 0 && <Badge className="bg-red-100 text-red-700">{batch.failed} failed</Badge>}
                        <Badge variant={batch.status === 'completed' ? 'default' : 'secondary'}>
                          {batch.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Logs (Super Admin Only) */}
          {isSuperAdmin && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Message Logs</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={fetchHistory}>
                      <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportLogs}>
                      <Download className="w-4 h-4 mr-2" />
                      Export PDF
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date/Time</TableHead>
                        <TableHead>Sent By</TableHead>
                        <TableHead>Recipients</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Filters/List</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.slice(0, 50).map((log, idx) => (
                        <TableRow key={log.id || idx}>
                          <TableCell className="text-sm">
                            {new Date(log.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-sm">
                            {log.sent_by_email || log.sent_by_its}
                          </TableCell>
                          <TableCell className="text-sm font-medium">
                            {log.recipient_count}
                          </TableCell>
                          <TableCell className="text-sm">
                            <Badge variant="outline">{log.source}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">
                            {log.filters_or_list}
                          </TableCell>
                        </TableRow>
                      ))}
                      {logs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                            No message logs yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* QR Code Dialog */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Scan QR Code to Connect WhatsApp</DialogTitle>
          </DialogHeader>
          {qrUrls && (
            <div className="text-center space-y-4">
              <iframe 
                src={qrUrls.qr_url} 
                className="w-full h-80 border rounded-lg"
                title="WhatsApp QR Code"
              />
              <p className="text-sm text-slate-600">
                Open WhatsApp → Settings → Linked Devices → Link a Device → Scan QR
              </p>
              <Button onClick={checkStatus} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Check Connection
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Message Preview (First 3 Recipients)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {previews.map((preview, idx) => (
              <div key={idx} className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">{preview.recipient_name}</span>
                  <span className="text-xs text-slate-500">{preview.recipient_mobile}</span>
                </div>
                <div className="bg-white border rounded-lg p-3 whitespace-pre-wrap text-sm">
                  {preview.personalized_message}
                </div>
              </div>
            ))}
            {previews.length === 0 && (
              <p className="text-center text-slate-500 py-4">No preview available</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Send Dialog */}
      <Dialog open={showConfirmSend} onOpenChange={setShowConfirmSend}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Send</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              You are about to send {selectionMode === 'manual' ? '' : 'personalized'} WhatsApp messages to:
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{recipientCount}</p>
              <p className="text-sm text-blue-600">
                {selectionMode === 'manual' ? 'phone numbers' : 'recipients'}
              </p>
            </div>
            {selectionMode === 'manual' && (
              <p className="text-xs text-amber-600 mt-2 text-center">
                Note: Manual numbers will receive the same message without personalization
              </p>
            )}
            {(attachedPdf || attachedImage) && (
              <div className="mt-4 text-sm text-slate-600">
                <p className="font-medium">Attachments:</p>
                {attachedPdf && <p>• PDF: {attachedPdf.name}</p>}
                {attachedImage && <p>• Image: {attachedImage.name}</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmSend(false)}>Cancel</Button>
            <Button 
              onClick={handleSend}
              disabled={sending}
              className="bg-green-600 hover:bg-green-700"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Send Now</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
